import { apiClient } from '@/utils/api'

// ─── Types ───────────────────────────────────────────────

export interface CommunityMessage {
  _id: string
  userId?: string | null
  username: string
  avatar: string
  content: string
  mentions: string[]
  groupId?: string | null
  createdAt: string
  /** agent 回复（合作闯关出题等）：user=真人，agent=辅导员 */
  senderType?: 'user' | 'agent'
  agentScenario?: string
}

export interface MentionUser {
  _id: string
  name: string
  username: string
  avatar: string
}

export interface ChatGroup {
  _id: string
  name: string
  description: string
  avatar: string
  ownerUserId: string
  ownerName?: string
  memberCount?: number
  myRole?: 'owner' | 'approved' | 'invited' | null
  status: string
  createdAt: string
}

export interface GroupInvitation {
  groupId: string
  name: string
  description: string
  ownerName: string
  memberCount: number
  invitedAt: string | null
}

export interface GroupMember {
  userId: string
  role: 'owner' | 'approved' | 'invited'
  joinedAt: string | null
  name: string
  avatar: string
}

export interface MuteStatus {
  muted: boolean
  until?: string
  reason?: string
}

// ─── Public Chat API ────────────────────────────────────

export async function fetchMessages(
  after?: string,
  limit = 50,
): Promise<CommunityMessage[]> {
  const params = new URLSearchParams()
  if (after) params.set('after', after)
  params.set('limit', String(limit))
  const qs = params.toString()
  const endpoint = `/aie/community/chat/messages${qs ? `?${qs}` : ''}`
  const res = await apiClient.get<{ data: CommunityMessage[] }>(endpoint)
  return res.data
}

export async function sendMessage(
  content: string,
  mentions: string[],
): Promise<CommunityMessage> {
  const res = await apiClient.post<{ data: CommunityMessage }>(
    '/aie/community/chat/messages',
    { content, mentions },
  )
  return res.data
}

export async function searchUsers(
  query: string,
): Promise<MentionUser[]> {
  const res = await apiClient.get<{ data: MentionUser[] }>(
    `/aie/community/chat/users?q=${encodeURIComponent(query)}`,
  )
  return res.data
}

export async function deleteMessage(
  messageId: string,
): Promise<void> {
  await apiClient.delete(`/aie/community/chat/messages/${messageId}`)
}

// ─── Mute Status ────────────────────────────────────────

export async function getMuteStatus(): Promise<MuteStatus> {
  const res = await apiClient.get<{ data: MuteStatus }>(
    '/aie/community/chat/muted-status',
  )
  return res.data
}

// ─── Group CRUD ─────────────────────────────────────────
// 注：创建群组已统一到「我的群组」页（groupService.createGroup），浮窗不再创建。

export async function fetchGroups(
  filter: 'mine' | 'joined' = 'joined',
  search = '',
  page = 1,
  limit = 20,
): Promise<{ data: ChatGroup[]; total: number; page: number; limit: number }> {
  const params = new URLSearchParams()
  params.set('filter', filter)
  if (search) params.set('search', search)
  params.set('page', String(page))
  params.set('limit', String(limit))
   
  const res = await apiClient.get<any>(
    `/aie/community/chat/groups?${params.toString()}`,
  )
  const body = res.data
  return {
    data: (body.data ?? body) as ChatGroup[],
    total: body.total ?? 0,
    page: body.page ?? 1,
    limit: body.limit ?? 20,
  }
}

export async function fetchMyInvitations(): Promise<GroupInvitation[]> {
  const res = await apiClient.get<{ data: GroupInvitation[] }>(
    '/aie/community/chat/groups/invitations',
  )
  return res.data ?? []
}

export interface UnreadSummary {
  total: number
  groups: Array<{ groupId: string; count: number }>
}

export async function fetchUnread(): Promise<UnreadSummary> {
  const res = await apiClient.get<{ data: UnreadSummary }>(
    '/aie/community/chat/groups/unread',
  )
  return res.data ?? { total: 0, groups: [] }
}

export async function markGroupRead(groupId: string): Promise<void> {
  await apiClient.put(`/aie/community/chat/groups/${groupId}/read`)
}

export async function fetchGroupDetail(
  groupId: string,
): Promise<ChatGroup & { members: GroupMember[]; myRole: string }> {
  const res = await apiClient.get<{ data: ChatGroup & { members: GroupMember[]; myRole: string } }>(
    `/aie/community/chat/groups/${groupId}`,
  )
  return res.data
}

export async function updateGroup(
  groupId: string,
  data: { name?: string; description?: string },
): Promise<ChatGroup> {
  const res = await apiClient.patch<{ data: ChatGroup }>(
    `/aie/community/chat/groups/${groupId}`,
    data,
  )
  return res.data
}

export async function deleteGroup(groupId: string): Promise<void> {
  await apiClient.delete(`/aie/community/chat/groups/${groupId}`)
}

// ─── Group Members ──────────────────────────────────────
// 【隐私收紧 2026-10-03】申请/审批链（applyToGroup/fetchApplicants/approveMember/
// rejectMember）已移除：加入途径统一为邀请码（groupService.joinByCode）或群主主动邀请。

export async function removeMember(groupId: string, userId: string): Promise<void> {
  await apiClient.delete(
    `/aie/community/chat/groups/${groupId}/members/${userId}`,
  )
}

// ─── Group Messages ─────────────────────────────────────

export async function fetchGroupMessages(
  groupId: string,
  after?: string,
  limit = 50,
): Promise<CommunityMessage[]> {
  const params = new URLSearchParams()
  if (after) params.set('after', after)
  params.set('limit', String(limit))
  const qs = params.toString()
  const res = await apiClient.get<{ data: CommunityMessage[] }>(
    `/aie/community/chat/groups/${groupId}/messages${qs ? `?${qs}` : ''}`,
  )
  return res.data
}

export async function sendGroupMessage(
  groupId: string,
  content: string,
  mentions: string[],
): Promise<CommunityMessage> {
  const res = await apiClient.post<{ data: CommunityMessage }>(
    `/aie/community/chat/groups/${groupId}/messages`,
    { content, mentions },
  )
  return res.data
}

export async function deleteGroupMessage(
  groupId: string,
  messageId: string,
): Promise<void> {
  await apiClient.delete(
    `/aie/community/chat/groups/${groupId}/messages/${messageId}`,
  )
}

// ─── Group Invitations ──────────────────────────────────

export async function inviteMember(
  groupId: string,
  userId: string,
): Promise<{ status: string; userName: string }> {
  const res = await apiClient.post<{ data: { status: string; userName: string } }>(
    `/aie/community/chat/groups/${groupId}/invite`,
    { userId },
  )
  return res.data
}

export async function fetchInvitedMembers(
  groupId: string,
): Promise<GroupMember[]> {
  const res = await apiClient.get<{ data: GroupMember[] }>(
    `/aie/community/chat/groups/${groupId}/invited`,
  )
  return res.data
}

export async function acceptInvite(groupId: string): Promise<{ status: string }> {
  const res = await apiClient.put<{ data: { status: string } }>(
    `/aie/community/chat/groups/${groupId}/accept-invite`,
  )
  return res.data
}

export async function rejectInvite(groupId: string): Promise<void> {
  await apiClient.put(
    `/aie/community/chat/groups/${groupId}/reject-invite`,
  )
}
